<template>
  <div class="clinic-main">
    <div class="title-box">
      <div class="flex-start">
        <button class="btn-back" type="button" name="button" data-test="btnBack" @click="goBack">
          <IconArrowBack/>
          {{ backButtonLabel }}
        </button>

        <h2 class="title">{{ clinic.name }}</h2>
      </div>
    </div>

    <BaseTab :tabs="tabs" :initialtab="tabActive" :key="tabActive" @tab-activated="switchTab">
      <template v-slot:tab-head-data>
        {{ $t('clinic.data') }}
      </template>
      <template v-slot:data>
        <ClinicInfoTab v-if="tabActive === 'data' && clinic && clinic.id"
          :clinic_id="clinic.id"/>
      </template>

      <template v-slot:tab-head-templates>
        {{ $t('clinic.template') }} ({{ $t('clinic.report') }})
      </template>
      <template v-slot:templates>
        <ClinicTemplateTab v-if="tabActive === 'templates'"/>
      </template>

      <template v-slot:tab-head-consentTemplates>
        {{ $t('clinic.template') }} ({{ $t('clinic.consent') }})
      </template>
      <template v-slot:consentTemplates>
        <ClinicConsentTemplateTab v-if="tabActive === 'consentTemplates'"/>
      </template>

      <template v-slot:tab-head-paymentHistory>
        {{ $t('clinic.myPlan') }}
      </template>
      <template v-slot:paymentHistory>
        <ClinicPaymentHistoryTab v-if="tabActive === 'paymentHistory'"/>
      </template>

      <template v-slot:tab-head-import>
        {{ $t('clinic.import') }}
      </template>
      <template v-slot:import>
        <ClinicImportTab v-if="tabActive === 'import' && isCurrentClinic && clinic.id" :clinic_id="clinic.id"/>
      </template>

      <template v-slot:tab-head-mutirao v-if="showMutirao">
        {{ $t('clinic.attendance') }}
      </template>
      <template v-slot:mutirao v-if="showMutirao">
        <ClinicMutiraoTab v-if="tabActive === 'mutirao'" :clinic="clinic"/>
      </template>

      <template v-slot:tab-head-config>
        {{ $t('createClinic.configuration') }}
      </template>
      <template v-slot:config v-if="clinic.id">
        <ClinicConfigurationTab v-if="tabActive === 'config'" :clinic_id="clinic.id"/>
      </template>

      <template v-slot:tab-head-flags v-if="showFlagsTab">
        {{ $t('clinic.modules') }}
      </template>
      <template v-slot:flags v-if="showFlagsTab && clinic.id">
        <ClinicFlagsTab v-if="tabActive === 'flags'" :clinic_id="clinic.id"/>
      </template>

      <template v-slot:tab-head-integrations v-if="showIntegrationsTab">
        {{ $t('integration.tab') }}
      </template>
      <template v-slot:integrations v-if="showIntegrationsTab && clinic.id">
        <ClinicIntegrationsTab v-if="tabActive === 'integrations'" :clinic_id="clinic.id"/>
      </template>

      <template v-slot:tab-head-connectors v-if="showIntegrationsTab">
        {{ $t('connector.tab') }}
      </template>
      <template v-slot:connectors v-if="showIntegrationsTab && clinic.id">
        <ClinicConnectorsTab v-if="tabActive === 'connectors'" :clinic_id="clinic.id" :clinic_uuid="clinic.uuid"/>
      </template>

      <template v-slot:tab-head-schedules v-if="showIntegrationsTab">
        {{ $t('schedule.tab') }}
      </template>
      <template v-slot:schedules v-if="showIntegrationsTab && clinic.id">
        <ClinicSchedulesTab v-if="tabActive === 'schedules'" :clinic_id="clinic.id"/>
      </template>
    </BaseTab>
  </div>
</template>

<style lang="scss" scoped>
.tab-pane {
  height:                            100%;
}

.title-box {
  .flex-start {
    gap:                             15px;
  }

  .btn-back {
    margin:                          0;
  }

  .flex-start > div:last-child .btn-outline-secondary:last-child {
    display:                         none;
  }
}

@media (max-width: 991px) {
  .title-box .btn-outline-secondary span {
    display:                         none;
  }
}
</style>

<script>
import { useUserStore } from '@/store/user'
import ClinicConfigurationTab from './ClinicConfigurationTab.vue'
import ClinicImportTab from './ClinicImportTab.vue'
import ClinicInfoTab from './ClinicInfoTab.vue'
import ClinicMutiraoTab from './ClinicMutiraoTab.vue'
import ClinicTemplateTab from './ClinicTemplateTab.vue'
import ClinicPaymentHistoryTab from './ClinicPaymentHistoryTab'
import ClinicConsentTemplateTab from './ClinicConsentTemplateTab.vue'
import ClinicFlagsTab from './ClinicFlagsTab.vue'
import ClinicIntegrationsTab from './ClinicIntegrationsTab.vue'
import ClinicConnectorsTab from './ClinicConnectorsTab.vue'
import ClinicSchedulesTab from './ClinicSchedulesTab.vue'

export default {
  name: 'ClinicShow',
  components: {
    ClinicConfigurationTab,
    ClinicImportTab,
    ClinicInfoTab,
    ClinicMutiraoTab,
    ClinicTemplateTab,
    ClinicPaymentHistoryTab,
    ClinicConsentTemplateTab,
    ClinicFlagsTab,
    ClinicIntegrationsTab,
    ClinicConnectorsTab,
    ClinicSchedulesTab,
  },
  data: () => ({
    clinic: {}, // TODO NEXT
    msgError: null,
    session: {
      collaborator: {},
      currentClinic: {}
    },
    isSpecialist: null,
    showMutirao: false,
    isCurrentClinic: false,
    selectedNotification: null,
    hasConsentFlag: false,
    showFlagsTab: false,
    showIntegrationsTab: false,
    tabActive: 'data',
    tabs: ['data'],
  }),
  computed: {
    tab() {
      return this.$route.query.tab;
    },
    backButtonLabel() {
      return this.$utils.getPreviousRouteTitle(this) || this.$i18n.t('goBack')
    },
  },
  created() {
    // watch param clinicID coming from notifications to force reload data
    this.$watch( // TODO REFACTOR KISS
      () => this.$route.params.clinicID,
      (toParams, previousParams) => {
        if (toParams != previousParams && this.$route.name == 'clinic-show')
          this.fetchData()
      }
    )
  },
  async mounted() {
    const userStore = useUserStore()
    this.session = userStore.sessionInfo
    this.isSpecialist = this.session.isSpecialist
    this.$activeLink.value = 'clinic'

    if (this.tab) {
      this.tabActive = this.tab;
    }

    await this.fetchData()
  },
  methods: {
    switchTab(tab) {
      this.tabActive = tab;
    },
    goBack() {
      this.$router.go(-1)
    },
    buildTabs() {
      const tabs = ['data']
      // tabs other than 'data' only work for the current clinic
      if (!this.isCurrentClinic) {
        this.tabs = tabs
        return
      }

      if (!this.isSpecialist) {
        tabs.push('templates')
        tabs.push('paymentHistory')
        tabs.push('import')
      }

      this.hasConsentFlag = this.session.features.includes('consentFlag');
      if (this.hasConsentFlag && !this.isSpecialist) {
        tabs.push('consentTemplates')
      }

      this.showMutirao = this.session.features.includes('mutirao')
      if (this.showMutirao && !this.isSpecialist) {
        tabs.push('mutirao')
        tabs.push('config')
      }

      const permissions = this.session.permissions
      this.showFlagsTab = typeof permissions === 'string' && permissions.includes('owner') && !(this.session.isGlobalClient ?? true)
      if (this.showFlagsTab) {
        tabs.push('flags')
      }

      this.showIntegrationsTab = this.session.features.includes('claims') || this.session.features.includes('generalReport')
      if (this.showIntegrationsTab && !this.isSpecialist) {
        tabs.push('integrations')
        tabs.push('connectors')
        tabs.push('schedules')
      }

      this.tabs = tabs
    },
    async fetchData() {
      let params = {
        id: this.$route.params.clinicID
      }

      // hide restricted tabs while loading, so they can't be opened for the wrong clinic
      this.isCurrentClinic = false
      this.tabs = ['data']

      try {
        let response = await this.$clinicService.getClinic(params);
        this.clinic = response.clinic
        this.isCurrentClinic = this.clinic.id === this.session.currentClinic.id
      } catch (err) {
        this.$message.popup(this, 'request_error', 'operationFail', 'error')
      } finally {
        this.buildTabs()
        if (!this.tabs.includes(this.tabActive)) {
          this.tabActive = 'data'
        }
      }

    }
  }
}
</script>
